import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { fadeInAnimation } from '../../animations/fade-in.animation';
import { _DEFAULT_NUTRITIONAL_PROPERTIES, FoodGroupAction, FoodGroupState, IFoodGroup, IFoodGroupIngredient } from '../../models/food-group';
import { Store } from '@ngxs/store';
import { firstValueFrom, Observable } from 'rxjs';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { ServiceLoopbackFilters } from '../../services/base-service/base.service';
import { MessageAlertService } from '../../services/message-alert/message-alert.service';
import { BlockPeriodTypeEnum } from '../../enums/meal-plan';
import { BlockPeriodClass } from '../../class/meal-plan/block-period.class';
import { cloneDeep } from 'lodash';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { GuidService } from '../../services/guid/guid.service';
import { IngredientCalculationService } from '../../services/ingredient-calculation/ingredient-calculation.service';
import { IIngredient, IngredientsAction, IngredientState } from '../../models/ingredient';
import { IMeasurementUnit } from '../../models/measurement';
import { Logger } from '../../class/helper/logger.class';
import { IngredientService } from '../../services/ingredient/ingredient.service';
import { Loader } from 'src/app/models/loader/loader.redux';
import { MealPlanBuilderState } from 'src/app/models/meal-plan-builder';

@Component({
  selector: 'app-food-group-form',
  templateUrl: './food-group-form.component.html',
  styleUrls: ['./food-group-form.component.scss'],
  animations: [fadeInAnimation]
})
export class FoodGroupFormComponent implements OnInit, OnChanges, OnDestroy {
  // variables para el buscador de platillos
  isLoadingMenus = false;
  finishSearchMenus = false;
  isLoadingFirstMenus = false;
  searchInterval: any;
  searchTempValue: string;
  totalBatches = 0;
  showSelectMenu: boolean;
  // variables para el buscador / selector de ingredientes
  showSelectIngredients: boolean;
  blockPeriodTypeEnum = BlockPeriodTypeEnum;
  confirmModal?: NzModalRef;
  measureUnits: IMeasurementUnit[];
  loadingIngredient: boolean; // Para el spinner en el botón Editar ingrediente

  foodGroups$: Observable<IFoodGroup[]> = this.store.select<IFoodGroup[]>(FoodGroupState.getFoodGroups);
  nzSelectMenuOpen = false;


  @ViewChild('selectMenu_') selectMenuElement: NzSelectComponent; // Dynamic ID not needed, remove _

  @Input() clinicId: string;
  @Input() blockPeriod: BlockPeriodClass = new BlockPeriodClass(null, this.guidService.guid(), true);

  @Output() changeBlockPeriod: EventEmitter<{ blockPeriod: BlockPeriodClass, action: 'add' | 'edit' | 'delete' | 'cancel' }> = new EventEmitter();


  constructor(
    private store: Store,
    private messageAlertService: MessageAlertService,
    private modal: NzModalService,
    private guidService: GuidService,
    private ingredientCalculationService: IngredientCalculationService,
    private ingredientService: IngredientService
  ) { }

  async ngOnInit(): Promise<void> {
    this.searchTempValue = '';
    await this.getMeasureUnits();
  }


  /**
   * Obtiene las unidades de medida desde redux.
   */
  async getMeasureUnits(): Promise<void> {
    this.measureUnits = cloneDeep(await firstValueFrom(this.store.selectOnce(IngredientState.getMeasureUnits)));
  }

  /**
   * Detecta cuando se cambia la propiedad blockPeriod para establecerle los datos y mostrarlos.
   * @param changes cambios
   */
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!changes.blockPeriod) return;
    if (!this.blockPeriod._id) this.blockPeriod.setId(this.guidService.guid());
    if (this.blockPeriod.foodGroupId && !this.blockPeriod.foodGroup) {
      await this.onOpenSelectableFoodGroups(!this.blockPeriod.editMode);
    }
    if (!this.blockPeriod.foodGroup) {
      const foodGroups = cloneDeep(await firstValueFrom((this.store.selectOnce(FoodGroupState.getFoodGroups))));
      this.blockPeriod.setFoodGroup(cloneDeep(foodGroups.find(foodGroup => foodGroup?._id === this.blockPeriod.foodGroupId)));
      // No need to set datetime here for FoodGroup type specifically, keep it generic if needed elsewhere.
    }
    this.showSelectMenu = true; // Ensure selector is shown when blockPeriod changes
  }

  /**
   * Abre el selector de html para mostrar la lista de platillos.
   * @param openSelect boolean
   */
  async onOpenSelectableFoodGroups(openSelect = true): Promise<void> {
    this.showSelectMenu = true;
    this.nzSelectMenuOpen = openSelect;
    await this.getFirstDataFoodGroups(); // lanza la peticion para cargar los datos en modo edición del card
  }

  /**
   * Obtiene los primeros elementos de platillos.
   */
  async getFirstDataFoodGroups(): Promise<void> {
    try {
      const filter: ServiceLoopbackFilters = {
        limit: 20,
        skip: 0,
        order: 'name ASC',
        include: []
      };
      this.isLoadingFirstMenus = true;
      await firstValueFrom((this.store.dispatch(new FoodGroupAction.GetByClinicId(this.clinicId, filter))));
      this.isLoadingFirstMenus = false;
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
    }
  }

  /**
   * se selecciona un platillo
   * @param foodGroupId food group id
   */
  async onChooseFoodGroup(foodGroupId: string): Promise<void> {
    await firstValueFrom((this.store.dispatch(new Loader(true)))); // Assuming Loader action exists, if not remove or replace.
    const foodGroups: IFoodGroup[] = await firstValueFrom((this.store.selectOnce(FoodGroupState.getFoodGroups)));
    const foodGroupSelected: IFoodGroup = foodGroups.find((foodGroup: IFoodGroup) => foodGroup?._id === foodGroupId);
    this.blockPeriod.setTitle(foodGroupSelected.name);
    this.blockPeriod.setFoodGroup(foodGroupId ? cloneDeep(foodGroupSelected) : null);
    const ingredientFoodGroup: IFoodGroupIngredient[] = cloneDeep(foodGroupSelected.ingredients);
    const canShowAllergicMode: boolean = await firstValueFrom(this.store.selectOnce(MealPlanBuilderState.getCanShowAllergicMode)); // Assuming MealPlanBuilderState and getCanShowAllergicMode exist, if not remove if allergy check not needed.
    if (canShowAllergicMode) {
      const dislikes: string[] = await this.ingredientService.getDislikesIngredientsPatient(); // Assuming these services and methods exist, adjust if needed.
      const allergies: string[] = await this.ingredientService.getAllergiesPatient();
      ingredientFoodGroup.forEach((ingredient: IFoodGroupIngredient) => {
        ingredient.isDislike = dislikes.includes(ingredient.ingredientId);
        ingredient.isAllergic = allergies.includes(ingredient.ingredientId);
      });
    }
    this.blockPeriod.setIngredients(foodGroupId ? ingredientFoodGroup : []);
    this.blockPeriod.setIsAllergic(ingredientFoodGroup.some((ingredient: IFoodGroupIngredient) => ingredient.isAllergic));
    this.blockPeriod.setFoodGroupTitle(foodGroupId ? foodGroupSelected.name : '');
    this.blockPeriod.setText(foodGroupId ? foodGroupSelected.description : '');
    this.blockPeriod.setNutritionalProperties(foodGroupId ? foodGroupSelected.nutritionalProperties :
      cloneDeep(_DEFAULT_NUTRITIONAL_PROPERTIES));
    this.changeBlockPeriod.emit({ blockPeriod: cloneDeep(this.blockPeriod), action: 'edit' });
    this.showSelectMenu = !!foodGroupSelected;
    await firstValueFrom((this.store.dispatch(new Loader(false)))); // Assuming Loader action exists, if not remove or replace.
  }

  /**
   * buscador de platillos
   * @param $event texto a buscar
   */
  async onSearchMenu($event): Promise<void> {
    if ($event === this.searchTempValue) {
      return;
    }
    this.isLoadingFirstMenus = true;
    if (this.searchInterval) {
      clearInterval(this.searchInterval);
    }
    this.searchInterval = setTimeout(async () => {
      this.finishSearchMenus = false;
      this.searchTempValue = $event;
      const filter = {
        limit: 20,
        skip: 0,
        search: $event += '',
        order: 'name ASC',
      };
      await firstValueFrom((this.store.dispatch(new FoodGroupAction.GetByClinicId(this.clinicId, filter))));
      this.isLoadingFirstMenus = false;
    }, 400);
  }

  /**
   * cargar más platillos cuando se hace scroll hacia abajo
   */
  async onLoadMoreFoodGroups(): Promise<void> {
    if (this.finishSearchMenus) {
      return;
    }
    this.isLoadingMenus = true;
    this.totalBatches++;
    const filter = {
      limit: 20,
      skip: this.totalBatches * 20,
      search: this.searchTempValue,
      order: 'name ASC',
    };
    await firstValueFrom((this.store.dispatch(new FoodGroupAction.GetByClinicId(this.clinicId, filter, true))));
    const menus = cloneDeep(await firstValueFrom((this.store.selectOnce(FoodGroupState.getFoodGroups))));
    this.finishSearchMenus = menus.length % 20 !== 0 || menus.length === 0;
    this.isLoadingMenus = false;
  }

  /**
   * cuando se cierra el selector de platillo, se deben resetear los valores para la búsqueda
   * @param event evento
   */
  async onCloseSelect(event): Promise<void> {
    if (!event) {
      this.isLoadingMenus = false;
      this.isLoadingFirstMenus = false;
      this.finishSearchMenus = false;
      this.searchTempValue = '';
      this.totalBatches = 0;
    }
  }


  onCancel(): void {
    this.changeBlockPeriod.emit({ blockPeriod: this.blockPeriod, action: 'cancel' });
  }


  /**
   * Emite al componente padre cuando se quiere agregar un nuevo block period o se modifica.
   */
  async onEmitPutBlockPeriod(): Promise<void> {
    if (this.blockPeriod.editMode) {
      this.changeBlockPeriod.emit({ blockPeriod: this.blockPeriod, action: 'edit' });
      this.blockPeriod.setEditMode(false);
      return;
    }
  }

  /**
   * Agregar en block period el ingrediente que se quiere modificar.
   * @param ingredient ingrediente
   */
  onSetIngredientToEdit(ingredient: Partial<IFoodGroupIngredient>): void {
    if (this.blockPeriod.ingredientToEdit?.ingredientId !== ingredient.ingredientId) {
      this.blockPeriod.ingredientToEdit = ingredient;
    } else {
      this.blockPeriod.ingredientToEdit = null;
    }
  }

  /**
   * Cuando se quiere mostrar la información de un ingrediente en el formulario.
   */
  async onShowFormIngredient(): Promise<void> {
    try {
      if (!this.blockPeriod.ingredientToEdit) {
        this.showSelectIngredients = true;
        return;
      }
      this.loadingIngredient = true;
      let ingredient: IIngredient;

      await firstValueFrom(this.store.dispatch(
        new IngredientsAction.SetSelectedIdUserListIngredient(
          this.blockPeriod.ingredientToEdit.ingredientId
        )));
      ingredient = cloneDeep(await firstValueFrom(this.store.selectOnce(IngredientState.getSelectedUserListIngredient)));

      if (!ingredient) {
        await firstValueFrom(this.store.dispatch(new IngredientsAction.GetById(this.blockPeriod.ingredientToEdit.ingredientId,
          {
            include: [{
              relation: 'unitOfMeasurement'
            },
            {
              relation: 'ingredientCategory'
            }]
          })));
        ingredient = cloneDeep(await firstValueFrom(this.store.selectOnce(IngredientState.getSelectedIngredient)));
      } else {
        await firstValueFrom((this.store.dispatch(new IngredientsAction.Set(ingredient))));
      }

      const nutrients = this.ingredientCalculationService.calculateNutritionalProperties(ingredient, this.measureUnits,
        this.blockPeriod.ingredientToEdit.measure?._id, this.blockPeriod.ingredientToEdit.units);

      this.blockPeriod.ingredientToEdit.nutritionalProperties = nutrients;
      this.blockPeriod.ingredientToEdit.category = ingredient.ingredientCategory;
      this.blockPeriod.ingredientToEdit.lastNutritionalProperties = nutrients;
      this.loadingIngredient = false;
      this.showSelectIngredients = true;
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    }
  }

  /**
   * Checa las propiedades del blockType - not used in HTML, consider removing from TS as well if truly unused.
   */
  async onCheckPropertiesBlockTypeIndividual(): Promise<void> {
    if (!(this.blockPeriod.ingredientToEdit.nutritionalProperties && this.blockPeriod.ingredientToEdit.category)) {
      await this.onShowFormIngredient();
      return;
    }
    this.blockPeriod.ingredientToEdit.lastNutritionalProperties =
      cloneDeep(this.blockPeriod.ingredientToEdit.nutritionalProperties);
    await firstValueFrom(this.store.dispatch(new IngredientsAction.SetSelectedIdUserListIngredient(
      this.blockPeriod.ingredientToEdit.ingredientId
    )));
    const ingredient = cloneDeep(await firstValueFrom((this.store.selectOnce(IngredientState.getSelectedUserListIngredient))));
    await firstValueFrom((this.store.dispatch(new IngredientsAction.Set(ingredient))));
    this.showSelectIngredients = true;
  }

  /**
   * Emite al componente padre cuando agrega o modifica un ingrediente a la lista cuando es un food group
   * @param ingredient ingrediente
   */
  onEmitSaveIngredient(ingredient: Partial<IFoodGroupIngredient>): void {
    try {
      if (!this.blockPeriod.ingredientToEdit) {
        this.blockPeriod.addIngredient(ingredient);
        this.blockPeriod.sumNutrients(ingredient.nutritionalProperties);
      } else {
        this.blockPeriod.editIngredient(ingredient, this.blockPeriod.ingredientToEdit.ingredientId);
        this.blockPeriod.subtractNutrients(ingredient.lastNutritionalProperties);
        this.blockPeriod.sumNutrients(ingredient.nutritionalProperties);
      }
      console.log(this.blockPeriod.ingredients);
      this.resetIngredientForm();
      this.changeBlockPeriod.emit({ blockPeriod: cloneDeep(this.blockPeriod), action: 'edit' });
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    }
  }

  /**
   * Descarta los cambios del formulario del ingrediente
   */
  onCancelEditIngredient(): void {
    this.resetIngredientForm();
  }

  /**
   * Elimina un ingrediente del food group
   * @param ingredient ingrediente object
   */
  async onRemoveIngredient(ingredient: Partial<IFoodGroupIngredient>): Promise<void> {
    if (this.blockPeriod.ingredients.length === 1) {
      this.messageAlertService.createMessage('warning', 'No se pueden eliminar todos los ingredientes, necesitas mínimo uno.', 5000);
      return;
    }
    this.confirmModal = this.modal.confirm({
      nzTitle: '¿Estás seguro de eliminar este ingrediente?',
      nzContent: 'Una vez guardado, esta acción no se podrá deshacer.',
      nzOkText: 'SÍ QUIERO ELIMINARLO',
      nzCancelText: 'NO QUIERO',
      nzOkDanger: true,
      nzClassName: 'modal-confirm modal-confirm-medium-size',
      nzClosable: false,
      nzOnOk: async () => {
        try {
          await firstValueFrom((this.store.dispatch(new IngredientsAction.GetById(ingredient.ingredientId))));
          const ingredientSelected = await firstValueFrom((this.store.selectOnce(IngredientState.getSelectedIngredient)));
          this.blockPeriod.deleteIngredient(this.blockPeriod.ingredientToEdit.ingredientId);
          const nutritionalPropertiesIngredient =
            this.ingredientCalculationService.calculateNutritionalProperties(
              ingredientSelected, this.measureUnits, ingredient.measure?._id, ingredient.units
            );
          this.blockPeriod.subtractNutrients(nutritionalPropertiesIngredient);
          this.resetIngredientForm();
          this.changeBlockPeriod.emit({ blockPeriod: cloneDeep(this.blockPeriod), action: 'edit' });
        } catch (e) {
          Logger.log('Ocurrio un error', e?.message);
        }
      },
      nzOnCancel: () => {
      }
    });
  }


  /**
   * Reestablece las variables para el listado de los ingredientes
   */
  resetIngredientForm(): void {
    this.blockPeriod.ingredientToEdit = null;
    this.showSelectIngredients = false;
    this.store.dispatch(new IngredientsAction.ResetIngredients());
  }

  ngOnDestroy(): void {
  }
}